import { Component, Inject, signal, inject, computed, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TextFieldModule } from '@angular/cdk/text-field';
import { LucideAngularModule, Search, X, FlaskConical, TriangleAlert } from 'lucide-angular';
import { KnowledgeBaseService } from '../../../../core/services/knowledge-base.service';
import { RetrievalData, RetrievalArticle } from '../../../../core/models/knowledge-base.model';
import { ArticleListComponent, ArticleDisplaySection, ArticleDisplayItem } from '../../../../shared/components/chat/article-list/article-list.component';

export interface RetrievalTestModalData {
  kbId: number;
  kbName: string;
  initialQuestion?: string;
  excludeIdentifier?: string;
  showProceedButton?: boolean;
}

@Component({
  selector: 'app-retrieval-test-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    TextFieldModule,
    LucideAngularModule,
    ArticleListComponent,
  ],
  templateUrl: './retrieval-test-modal.component.html',
  styleUrl: './retrieval-test-modal.component.scss',
})
export class RetrievalTestModalComponent implements OnInit {
  private readonly kbService = inject(KnowledgeBaseService);
  private readonly fb = inject(FormBuilder);
  readonly dialogRef = inject(MatDialogRef<RetrievalTestModalComponent>);

  readonly FlaskConical = FlaskConical;
  readonly Search = Search;
  readonly X = X;
  readonly TriangleAlert = TriangleAlert;

  readonly articleForm: FormGroup = this.fb.group({
    question: ['', Validators.required],
    environment: ['QA', Validators.required],
  });

  loading = signal(false);
  result = signal<RetrievalData | null>(null);
  error = signal<string | null>(null);

  readonly articleSections = computed<ArticleDisplaySection[]>(() => {
    const data = this.result();
    if (!data) return [];

    const exclude = this.data.excludeIdentifier;
    const mapArticle = (a: RetrievalArticle): ArticleDisplayItem => ({
      identifier: a.identifier,
      score: a.score,
      category: a.mainCategory,
      subCategory: a.subCategory,
      question: a.question,
      content: a.response,
      action: a.actions,
      requestKey: a.requestKey,
    });
    const filterExcluded = (articles: RetrievalArticle[]) =>
      exclude ? articles.filter(a => a.identifier !== exclude) : articles;

    return [
      {
        label: 'Recuperacion semantica + repriorización contextual',
        style: 'blue',
        articles: filterExcluded(data.rerankedArticles).map(mapArticle),
        proposedAction: data.proposedAction,
      },
      {
        label: 'Recuperacion semantica original',
        style: 'gray',
        articles: filterExcluded(data.retrievedArticles).map(mapArticle),
      },
    ];
  });

  constructor(@Inject(MAT_DIALOG_DATA) readonly data: RetrievalTestModalData) {}

  ngOnInit(): void {
    if (this.data.initialQuestion) {
      this.articleForm.patchValue({ question: this.data.initialQuestion });
      this.search();
    }
  }

  search(): void {
    if (this.articleForm.invalid || this.loading()) return;

    const { question, environment } = this.articleForm.getRawValue();

    this.loading.set(true);
    this.error.set(null);
    this.result.set(null);

    this.kbService.testRetrieval(this.data.kbId, question.trim(), environment)
      .subscribe({
        next: (response) => {
          this.result.set(response.data);
          this.loading.set(false);
        },
        error: (err) => {
          this.error.set('Error al consultar el retrieval. Intenta nuevamente.');
          this.loading.set(false);
          console.error('Retrieval test error:', err);
        },
      });
  }

  proceed(): void {
    this.dialogRef.close({ proceed: true });
  }

  close(): void {
    this.dialogRef.close();
  }
}
